import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import PageNotFound from "./lib/PageNotFound";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProviderRoute from "@/components/ProviderRoute";
import { AccessibilityProvider, useAccessibility } from "@/lib/accessibilityContext";
import AccessibilityPanel from "@/components/AccessibilityPanel";
import { OrganisationProvider } from "@/lib/OrganisationContext";

// Page imports
import AppLayout from "@/components/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Assessment from "@/pages/Assessment";
import Roadmap from "@/pages/Roadmap";
import EvidenceVault from "@/pages/EvidenceVault";
import Resources from "@/pages/Resources";
import Settings from "@/pages/Settings";
import SLTInsights from "@/pages/SLTInsights";
import SLTResponse from "@/pages/SLTResponse";
import Personas from "@/pages/Personas";
import ESGBids from "@/pages/ESGBids";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminHome from "@/pages/admin/AdminHome";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminProvider from "@/pages/admin/AdminProvider";
import AdminCustomerNew from "@/pages/admin/AdminCustomerNew";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSso from "@/pages/admin/AdminSso";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminPersonas from "@/pages/admin/Personas";
import PersonaEditor from "@/pages/admin/PersonaEditor";
import PersonaImports from "@/pages/admin/PersonaImports";
import ContentEditors from "@/pages/admin/content/ContentEditors";
import Framework from "@/pages/admin/Framework";
import FrameworkVersion from "@/pages/admin/FrameworkVersion";
import Login from "@/pages/Login";
import AuthError from "@/pages/AuthError";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Welcome from "@/pages/Welcome";

function LegacyEvidenceRoute() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/evidence-vault", search, hash }} replace />;
}

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-background"
        role="status"
        aria-live="polite"
      >
        <div
          className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"
          aria-label="Loading application"
        />
      </div>
    );
  }

  // IC-005: no top-level authError gate here any more — it used to block routing to every
  // page (including /login) whenever a 403 authError was set. /me never actually returns
  // 403 (a signed-in user with no membership gets 200 + an empty organisations array), so
  // that branch was both unreachable in practice and a "blank app" risk in principle.
  // ProtectedRoute now handles the real "no organisation" case per-route (redirect to
  // /welcome), and /login etc. render unconditionally either way.

  return (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthenticatedApp />}>
      <Route path="/login" element={<Login />} />
      <Route path="/login/error" element={<AuthError />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/slt-quiz" element={<SLTResponse />} />
      <Route path="/accept-invitation/:id" element={<AcceptInvitation />} />
      <Route
        element={
          <ProtectedRoute
            unauthenticatedElement={<Navigate to="/login" replace />}
            skipOrgRedirect
          />
        }
      >
        <Route path="/welcome" element={<Welcome />} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            unauthenticatedElement={<Navigate to="/login" replace />}
          />
        }
      >
        <Route element={<AppLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/assessment" element={<Assessment />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/evidence-vault" element={<EvidenceVault />} />
          <Route path="/evidence" element={<LegacyEvidenceRoute />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/slt" element={<SLTInsights />} />
          <Route path="/personas" element={<Personas />} />
          <Route path="/esg-bids" element={<ESGBids />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route element={<ProviderRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminHome />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/new" element={<AdminCustomerNew />} />
            <Route path="customers/:id" element={<AdminCustomerDetail />} />
            <Route path="provider" element={<AdminProvider />} />
            <Route path="verification" element={<AdminVerification />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="framework" element={<Framework />} />
            <Route path="framework/:id" element={<FrameworkVersion />} />
            <Route path="content" element={<ContentEditors />} />
            <Route path="personas" element={<AdminPersonas />} />
            <Route path="persona-imports" element={<PersonaImports />} />
            <Route path="personas/:id" element={<PersonaEditor />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="sso" element={<AdminSso />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Route>,
  ),
);

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <AccessibilityProvider>
          <OrganisationProvider>
            <RouterProvider router={router} />
          </OrganisationProvider>
          <AccessibilitySetup />
        </AccessibilityProvider>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  );
}

function AccessibilitySetup() {
  const { ready, setupOpen, setSetupOpen, updatePrefs } = useAccessibility();
  if (!ready || !setupOpen) return null;
  return <AccessibilityPanel setup onClose={() => { setSetupOpen(false); updatePrefs({ onboardingSkipped: true }); }} onComplete={() => { setSetupOpen(false); updatePrefs({ onboardingCompleted: true, onboardingSkipped: false }); }} />;
}

export default App;
